import { WORDS } from "../../../backend/entry-game-server/src/word-list";
import type { BoardPosition, EntryRole, GameState, Move } from "../../../shared/api/entry-api";

export type LocalMode = "bot" | "passplay";
export type LocalPlayer = EntryRole | "bot";

export interface LocalGameState {
  mode: LocalMode;
  board: { rows: Array<Array<string | null>> };
  currentPlayer: LocalPlayer;
  turnStartedAt: number;
  usedWords: Set<string>;
  scores: Record<LocalPlayer, number>;
  history: Move[];
  passCounts: Partial<Record<LocalPlayer, number>>;
  isFinished: boolean;
  winner: LocalPlayer | null;
  endReason: "passes" | "board" | "resigned" | "timeout" | null;
}

const BOARD_SIZE = 5;
const TURN_DURATION_MS = 60_000;
const BOT_MAX_WORD_LENGTH = 12;
const BOT_MOVE_LIMIT = 160;
const alphabet = "abcdefghijklmnopqrstuvwxyz";
const dictionary = WORDS as ReadonlySet<string>;
const playableWords = Array.from(dictionary).filter((word) => /^[a-z]+$/.test(word));
const startWords = playableWords.filter((word) => word.length === BOARD_SIZE);
const prefixes = new Set<string>();

for (const word of playableWords) {
  for (let length = 1; length < word.length; length += 1) prefixes.add(word.slice(0, length));
}

export function newLocalGame(mode: LocalMode, previousStartWord?: string): LocalGameState {
  const candidates = startWords.filter((word) => word !== previousStartWord);
  const pool = candidates.length ? candidates : startWords;
  const startWord = pool[Math.floor(Math.random() * pool.length)] ?? "entry";
  const rows = Array.from({ length: BOARD_SIZE }, () => Array<string | null>(BOARD_SIZE).fill(null));
  for (const [column, letter] of Array.from(startWord).entries()) rows[Math.floor(BOARD_SIZE / 2)][column] = letter;
  return {
    mode,
    board: { rows },
    currentPlayer: "human",
    turnStartedAt: Date.now(),
    usedWords: new Set([startWord]),
    scores: { human: 0, guest: 0, bot: 0 },
    history: [],
    passCounts: {},
    isFinished: false,
    winner: null,
    endReason: null
  };
}

export function localViewState(state: LocalGameState): GameState {
  return {
    board: state.board,
    currentPlayer: state.currentPlayer === "bot" ? "guest" : state.currentPlayer,
    turnStartedAt: new Date(state.turnStartedAt).toISOString(),
    usedWords: Array.from(state.usedWords),
    scores: state.scores,
    history: state.history,
    passCounts: state.passCounts,
    isFinished: state.isFinished,
    forfeitingPlayer: Object.entries(state.passCounts).find(([, count]) => (count ?? 0) >= 3)?.[0] as EntryRole | undefined,
    winner: state.winner === "bot" ? "guest" : state.winner
  };
}

export function localOpponent(state: LocalGameState): LocalPlayer {
  return state.mode === "bot" ? "bot" : "guest";
}

export function localDeadline(state: LocalGameState): number {
  return state.turnStartedAt + TURN_DURATION_MS;
}

export function localSecondsLeft(state: LocalGameState, now = Date.now()): number {
  return Math.max(0, Math.ceil((localDeadline(state) - now) / 1000));
}

export function localNeighbors(position: BoardPosition): BoardPosition[] {
  return [
    { row: position.row - 1, column: position.column },
    { row: position.row + 1, column: position.column },
    { row: position.row, column: position.column - 1 },
    { row: position.row, column: position.column + 1 }
  ].filter(isInside);
}

export function isInside(position: BoardPosition): boolean {
  return position.row >= 0 && position.row < BOARD_SIZE && position.column >= 0 && position.column < BOARD_SIZE;
}

export function isAdjacent(a: BoardPosition, b: BoardPosition): boolean {
  return Math.abs(a.row - b.row) + Math.abs(a.column - b.column) === 1;
}

export function validateLocalMove(
  state: LocalGameState,
  input: { letter: string; placement: BoardPosition; path: BoardPosition[]; word: string }
): string {
  const letter = input.letter.trim().toLowerCase();
  const word = input.word.trim().toLowerCase();
  if (letter.length !== 1 || !alphabet.includes(letter)) throw new Error("Choose one English letter.");
  if (word.length < 3) throw new Error("Words must contain at least three letters.");
  if (!isInside(input.placement)) throw new Error("Choose a cell on the board.");
  if (state.board.rows[input.placement.row][input.placement.column] !== null) throw new Error("That cell is already occupied.");
  if (!localNeighbors(input.placement).some((position) => state.board.rows[position.row][position.column] !== null)) {
    throw new Error("The new letter must touch an existing letter.");
  }
  if (!input.path.some((position) => samePosition(position, input.placement))) throw new Error("The word must include the new letter.");
  const seen = new Set<string>();
  for (let index = 0; index < input.path.length; index += 1) {
    const position = input.path[index];
    if (!isInside(position)) throw new Error("The word path leaves the board.");
    const key = positionKey(position);
    if (seen.has(key)) throw new Error("A word cannot use a cell twice.");
    seen.add(key);
    if (index > 0 && !isAdjacent(input.path[index - 1], position)) throw new Error("Words must move through touching cells.");
  }
  const board = state.board.rows.map((row) => [...row]);
  board[input.placement.row][input.placement.column] = letter;
  const actual = input.path.map((position) => board[position.row][position.column] ?? "").join("");
  if (actual !== word) throw new Error(`The selected path spells ${actual.toUpperCase()}, not ${word.toUpperCase()}.`);
  if (state.usedWords.has(word)) throw new Error("That word has already been played.");
  if (!dictionary.has(word)) throw new Error("That noun is not in the Entry dictionary.");
  return word;
}

export function applyLocalMove(
  state: LocalGameState,
  input: { letter: string; placement: BoardPosition; path: BoardPosition[]; word: string },
  player: LocalPlayer,
  now = Date.now()
): void {
  const word = validateLocalMove(state, input);
  state.board.rows[input.placement.row][input.placement.column] = input.letter.toLowerCase();
  state.usedWords.add(word);
  state.scores[player] = (state.scores[player] ?? 0) + word.length;
  state.history.push({ ...input, letter: input.letter.toLowerCase(), word, player: player === "bot" ? "guest" : player });
  state.passCounts[player] = 0;
  state.currentPlayer = localOpponent(state) === player ? "human" : localOpponent(state);
  state.turnStartedAt = now;
  finishIfNeeded(state);
}

export function applyLocalPass(state: LocalGameState, now = Date.now()): void {
  const player = state.currentPlayer;
  state.passCounts[player] = (state.passCounts[player] ?? 0) + 1;
  state.currentPlayer = player === "human" ? localOpponent(state) : "human";
  state.turnStartedAt = now;
  if ((state.passCounts[player] ?? 0) >= 3) {
    state.isFinished = true;
    state.winner = player === "human" ? localOpponent(state) : "human";
    state.endReason = "passes";
  }
}

export function resignLocalGame(state: LocalGameState): void {
  state.isFinished = true;
  state.winner = state.currentPlayer === "human" ? localOpponent(state) : "human";
  state.endReason = "resigned";
}

export function chooseBotMove(state: LocalGameState): { letter: string; placement: BoardPosition; path: BoardPosition[]; word: string } | null {
  const moves: Array<{ letter: string; placement: BoardPosition; path: BoardPosition[]; word: string }> = [];
  for (const placement of emptyPositionsAdjacentToLetters(state)) {
    for (const letter of alphabet) {
      const board = state.board.rows.map((row) => [...row]);
      board[placement.row][placement.column] = letter;
      for (let row = 0; row < BOARD_SIZE; row += 1) {
        for (let column = 0; column < BOARD_SIZE; column += 1) {
          const start = { row, column };
          const startLetter = board[row][column];
          if (!startLetter) continue;
          collectBotPaths(state, board, placement, start, [{ ...start }], startLetter, moves);
          if (moves.length >= BOT_MOVE_LIMIT) break;
        }
        if (moves.length >= BOT_MOVE_LIMIT) break;
      }
      if (moves.length >= BOT_MOVE_LIMIT) break;
    }
    if (moves.length >= BOT_MOVE_LIMIT) break;
  }
  if (!moves.length) return null;
  const min = Math.min(...moves.map((move) => move.word.length));
  const max = Math.max(...moves.map((move) => move.word.length));
  const target = min + Math.floor(Math.random() * (max - min + 1));
  return moves.filter((move) => move.word.length === target).at(Math.floor(Math.random() * moves.filter((move) => move.word.length === target).length))
    ?? moves[Math.floor(Math.random() * moves.length)];
}

function collectBotPaths(
  state: LocalGameState,
  board: Array<Array<string | null>>,
  placement: BoardPosition,
  current: BoardPosition,
  path: BoardPosition[],
  text: string,
  moves: Array<{ letter: string; placement: BoardPosition; path: BoardPosition[]; word: string }>
): void {
  if (text.length > BOT_MAX_WORD_LENGTH || (!dictionary.has(text) && !prefixes.has(text))) return;
  if (text.length >= 3 && path.some((position) => samePosition(position, placement)) && !state.usedWords.has(text)) {
    moves.push({ letter: board[placement.row][placement.column] ?? "a", placement, path: [...path], word: text });
    if (moves.length >= BOT_MOVE_LIMIT) return;
  }
  if (path.length >= BOT_MAX_WORD_LENGTH) return;
  for (const next of localNeighbors(current)) {
    if (path.some((position) => samePosition(position, next)) || !board[next.row][next.column]) continue;
    collectBotPaths(state, board, placement, next, [...path, next], text + board[next.row][next.column], moves);
    if (moves.length >= BOT_MOVE_LIMIT) return;
  }
}

function emptyPositionsAdjacentToLetters(state: LocalGameState): BoardPosition[] {
  const positions: BoardPosition[] = [];
  for (let row = 0; row < BOARD_SIZE; row += 1) {
    for (let column = 0; column < BOARD_SIZE; column += 1) {
      const position = { row, column };
      if (state.board.rows[row][column] === null && localNeighbors(position).some((neighbor) => state.board.rows[neighbor.row][neighbor.column] !== null)) positions.push(position);
    }
  }
  return positions;
}

function finishIfNeeded(state: LocalGameState): void {
  if (state.board.rows.every((row) => row.every((value) => value !== null))) {
    state.isFinished = true;
    const opponent = localOpponent(state);
    const humanScore = state.scores.human ?? 0;
    const opponentScore = state.scores[opponent] ?? 0;
    state.winner = humanScore === opponentScore ? null : humanScore > opponentScore ? "human" : opponent;
    state.endReason = "board";
  }
}

function samePosition(a: BoardPosition, b: BoardPosition): boolean { return a.row === b.row && a.column === b.column; }
function positionKey(position: BoardPosition): string { return `${position.row}:${position.column}`; }
